/** Only this port knows whether the channel store is synchronous or asynchronous. */
export type ProductStorage = {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): unknown | Promise<unknown>;
  removeItem(key: string): unknown | Promise<unknown>;
};

let selected: ProductStorage | null = null;
const webStorage: ProductStorage = {
  getItem: (key) => globalThis.localStorage.getItem(key),
  setItem: (key, value) => globalThis.localStorage.setItem(key, value),
  removeItem: (key) => globalThis.localStorage.removeItem(key),
};
export function installProductStorage(storage: ProductStorage) { selected = storage; }
export function productStorage(): ProductStorage { return selected ?? webStorage; }

const queues = new WeakMap<ProductStorage, Promise<unknown>>();
/** Serialize read/modify/ACK in this app and, on Web, across tabs where Web Locks exists. */
export function storageTransaction<T>(storage: ProductStorage, operation: () => Promise<T>): Promise<T> {
  const previous = queues.get(storage) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(() => {
    const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
    return storage === webStorage && locks ? locks.request("hanjan-experience-write", operation) : operation();
  });
  queues.set(storage, next.catch(() => undefined));
  return next;
}

export type StorageRead<T> =
  | { status: "empty" | "valid"; data: T; originals: Record<string, string | null> }
  | { status: "unreadable" | "unavailable" | "unsupported_version"; data: T; originals: Record<string, string | null> };
export class RecordStorageError extends Error {
  constructor(public readonly kind: "unreadable" | "unavailable" | "unsupported_version" | "ack_unknown" | "conflict") {
    super(kind === "unsupported_version" ? "다른 버전에서 저장한 기록이에요. 원본을 보존했어요." : kind === "unreadable" ? "기록을 읽지 못했어요. 원본은 그대로 있어요." : kind === "conflict" ? "다른 화면에서 기록이 바뀌었어요. 다시 확인해 주세요." : "저장 상태를 확인하지 못했어요. 입력을 유지하고 다시 시도할 수 있어요.");
  }
}
export async function writeWithReadback(storage: ProductStorage, key: string, value: string) {
  try { await storage.setItem(key, value); if (await storage.getItem(key) !== value) throw new Error("readback"); }
  catch { throw new RecordStorageError("ack_unknown"); }
}
export async function deleteOwnedKeys(storage: ProductStorage, keys: readonly string[]) {
  const remaining: string[] = [];
  for (const key of keys) {
    try { await storage.removeItem(key); if (await storage.getItem(key) !== null) remaining.push(key); }
    catch { remaining.push(key); }
  }
  return { completed: remaining.length === 0, remaining };
}
